import clsx from 'clsx';
import { Fuel, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import type { TruckFuelEconomyState } from '../hooks/use-truck-fuel-economy';
import { fuelEconomyLabels } from '../policies/fuel-economy-labels';
import type { FuelTrendDirection } from '../trucks.api';

const TREND_ICON = { rising: TrendingUp, falling: TrendingDown, steady: Minus } as const;

/** More km per litre is the better way to move; fewer is the worse. */
const TREND_TONE: Record<Exclude<FuelTrendDirection, 'unknown'>, string> = { rising: 'text-success', falling: 'text-warning', steady: 'text-muted' };

function FuelEconomySkeleton() {
  return (
    <SkeletonRegion label="Loading fuel consumption" className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <Skeleton className="size-9" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
    </SkeletonRegion>
  );
}

/** The truck's rolling consumption beside its odometer: the figure, what it rests on, and which way it is heading. */
export function TruckFuelEconomyCard({ state }: { state: TruckFuelEconomyState }) {
  if (state.status === 'loading') return <FuelEconomySkeleton />;
  if (state.status === 'error') return null;

  const { economy } = state;
  const labels = fuelEconomyLabels(economy);
  const direction = economy.trend.direction;
  const TrendIcon = direction === 'unknown' ? null : TREND_ICON[direction];

  return (
    <section aria-label="Fuel consumption" className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
        <Fuel className="size-4" />
      </div>
      <div>
        <p className={clsx('font-medium', economy.kmPerLitre === null ? 'text-muted' : 'text-fg')}>{labels.figure}</p>
        <p className="text-sm text-muted">{labels.basis}</p>
        {TrendIcon && direction !== 'unknown' && labels.trend && (
          <p className={clsx('flex items-center gap-1 text-sm', TREND_TONE[direction])}>
            <TrendIcon className="size-4" aria-hidden />
            {labels.trend}
          </p>
        )}
      </div>
    </section>
  );
}
